import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import { BranchesService } from "../../../services/branches-service";

import { CentralIntakeService } from "../../../services/central-intake.service";
import { IAgencyBranchesResponse, IInquiryLocationsResponse } from "../../vue-interfaces/i-get-inquiry";
import { IBranchResponse } from "../../vue-interfaces/i-branch";
import { formatDate } from "../../../common/vue-helpers/modifiers/value-modifier";
import { PermissionManager } from "../../../common/utilities/permission-manager";
import { ParentPermissionEnum } from "../../../enums/ParentPermissionEnum";
import { PermissionActionEnum } from "../../../enums/PermissionActionEnum";
import { ToastrService } from "../../../services/toastr.service";
import { IPromptOptions } from "../../vue-interfaces/i-prompt";

import ErrorMessageHandler from "../../vue-custom-elements/ErrorMessageHandler/ErrorMessageHandler.vue";
import EmptyTextHandler from "../../vue-custom-elements/EmptyTextHandler/EmptyTextHandler.vue";
import Prompt from "../../vue-dialogs/prompt/prompt.vue";

export default defineComponent({
    components: {
        ErrorMessageHandler,
        EmptyTextHandler
    },

    inject: ["dialogRef"],

    data(vm) {
        return {
            _toastrService: null as ToastrService,
            _permissionManager: null as PermissionManager,
            _centralIntakeService: null as CentralIntakeService,
            _branchesService: null as BranchesService,
            allBranchList: [] as IBranchResponse[],
            inquiryLocations: [] as IInquiryLocationsResponse[],
            isLoading: false as boolean,
            isError: false as boolean,
            inquiryDetails: null,
            dialogReference: this.dialogRef as any,
            canEditInquiryLocation: false as boolean,
            isEditRequested: false as boolean,
            isSaving: false as boolean,
            branchesLoading: false as boolean,
            agencyBranches: [] as IBranchResponse[],
            selectedAgencyBranch: null as string,
            selectedLocation: null as IInquiryLocationsResponse,
            formatDate
        };
    },

    created() {
        this._centralIntakeService = Container.instance.get(CentralIntakeService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._toastrService = Container.instance.get(ToastrService);
        this._branchesService = Container.instance.get(BranchesService);
        this.inquiryDetails = this.dialogReference.data.inquiryDetails;
    },

    async mounted() {
        this.getInquiryBranches();
        this.canEditInquiryLocation = this._permissionManager.checkPermission(
            ParentPermissionEnum.inquiries,
            PermissionActionEnum.canEditBranches
        );
        if (this.canEditInquiryLocation) {
            this.allBranchList = await this._branchesService.getAllBranches();
            this.agencyBranches = this.allBranchList.filter(
                (branch) => branch.accountId === this.inquiryDetails.accountId
            );
        }
    },
    computed: {
        hasData() {
            return this.inquiryLocations.length;
        },
        columns() {
            return this.canEditInquiryLocation ? 8 : 7;
        }
    },
    methods: {
        async getInquiryBranches() {
            try {
                this.isLoading = true;
                const payload = {
                    agencyId: this.inquiryDetails.accountId,
                    application: this.inquiryDetails.application,
                    inquiryId: this.inquiryDetails.id
                };
                const response = await this._centralIntakeService.getInquiryLocations(payload);
                this.inquiryLocations = response;
            } catch (err) {
                console.error(err);
                this.isError = true;
            } finally {
                this.isLoading = false;
            }
        },
        closeDialog() {
            this.dialogReference.close();
        },
        handleEditRequest(location) {
            this.isEditRequested = true;
            this.selectedLocation = location;
        },
        cancelEditLocation() {
            this.isEditRequested = false;
            this.selectedLocation = null;
        },
        async updateInquiryBranch(location) {
            const payload = {
                AgencyId: this.inquiryDetails.accountId,
                Application: this.inquiryDetails.application,
                AgencyLocationId: this.selectedAgencyBranch,
                Id: location.id,
                InquiryId: location.inquiryId
            };
            let promptOptions: IPromptOptions = {
                message:
                    "All documentation will reflect the updated branch information starting from the branch’s effective date. Do you want to save this branch?",
                okText: "Yes",
                cancelText: "No"
            };
            this.$dialog.open(Prompt, {
                props: {
                    modal: true,
                    contentStyle: {
                        overflowX: "hidden"
                    },
                    showHeader: false
                },
                data: {
                    promptOptions
                },
                onClose: async (confirm) => {
                    if (confirm.data === "success") {
                        try {
                            this.isSaving = true;
                            const response = await this._centralIntakeService.updateInquiryLocation(payload);
                            this._toastrService.success({
                                title: "Updated!!",
                                message: `${response.ErrorMessage}`
                            });
                        } catch (err) {
                            console.error(err);
                            this._toastrService.success({
                                title: "Failed!!",
                                message: `Failed to update inquiry location . Try Again!!`
                            });
                        } finally {
                            this.isSaving = false;
                            this.isEditRequested = false;
                            this.selectedLocation = null;
                            this.getInquiryBranches();
                        }
                    }
                }
            });
        }
    }
});
